import React from 'react'
import {
  Banner,
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from '@lightspeed/design-system-react'
import VendNumber from 'vend-number'

import { T, useT } from '@transifex/react'
import {
  IconDelete20,
  IconSell20,
} from '@lightspeed/unified-components-helios-theme/react'
import {
  DECIMAL_PLACES,
  useFormatPercentage,
} from '../../../../helpers/formatNumberHooks'
import { RoundedNumber, HandlePaymentFunction } from '../../CashPaymentModal'
import { Header } from './Header'
import { Actions } from './Actions'

interface PayProps {
  amountToPay: RoundedNumber
  closeWithoutPayment: () => void
  currencySymbol: string
  handlePayment: HandlePaymentFunction
  rounding: VendNumber
  /**
   * When set, a dismissable banner is shown indicating the cash discount that
   * has been applied to the amount-to-pay.
   */
  cashDiscountRate?: number
  /** Invoked when the customer dismisses the cash discount banner. */
  onRemoveCashDiscount?: () => void
}

export const Pay: React.FC<PayProps> = ({
  amountToPay,
  closeWithoutPayment,
  currencySymbol,
  handlePayment,
  rounding,
  cashDiscountRate,
  onRemoveCashDiscount,
}) => {
  const t = useT()
  const formatPercentage = useFormatPercentage({
    minimumFractionDigits: DECIMAL_PLACES,
  })

  return (
    <Modal size="medium" dismissable onClose={() => closeWithoutPayment()}>
      <ModalHeader>
        <Header
          amountToPay={amountToPay}
          hasCashDiscount={cashDiscountRate != null}
        />
      </ModalHeader>
      {cashDiscountRate != null && onRemoveCashDiscount && (
        <ModalContent className="lspay-cash-discount-banner__container">
          <Banner
            variant="success"
            hasAction
            horizontalAlign="left"
            className="lspay-cash-discount-banner"
            data-testid="lspay-cash-discount-banner"
            feedback={
              <button
                type="button"
                className="lspay-cash-discount-banner__delete"
                aria-label={t('Remove cash discount', {
                  _context:
                    'Aria label for the trash button that removes the LSPay cash discount banner',
                })}
                onClick={onRemoveCashDiscount}
                data-track="remove-lspay-cash-discount"
                data-testid="lspay-cash-discount-banner-delete"
              >
                <IconDelete20 />
              </button>
            }
          >
            <span className="lspay-cash-discount-banner__content">
              <IconSell20 className="lspay-cash-discount-banner__tag-icon" />
              <T
                _str="Cash discount {rate} applied"
                _context="LSPay cash discount banner in cash payment modal"
                rate={formatPercentage(cashDiscountRate / 100)}
              />
            </span>
          </Banner>
        </ModalContent>
      )}
      <ModalContent>
        <span className="vd-text-heading">
          <T _str="Amount given by customer" />
        </span>
      </ModalContent>
      <ModalActions>
        <Actions
          rounding={rounding}
          amountToPay={amountToPay}
          handlePayment={handlePayment}
          currencySymbol={currencySymbol}
        />
      </ModalActions>
    </Modal>
  )
}
